import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Building2, Search, Users, Euro, Calendar, Mail, Phone, ExternalLink, Loader2, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { inHouseApi } from '../../lib/api';

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '—'; }
};

/**
 * Firmen-Übersicht: alle Unternehmen, die je In-House-Kurse gebucht haben,
 * inkl. Umsatz, Teilnehmerzahl, Anzahl Kurse und Klapp-Detailansicht.
 */
export function CompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expandedKey, setExpandedKey] = useState(null);

    useEffect(() => {
        inHouseApi.listCompanies()
            .then(r => setCompanies(r.data || []))
            .catch(e => toast.error(e.response?.data?.detail || 'Firmen konnten nicht geladen werden'))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return companies;
        return companies.filter(c =>
            (c.name || '').toLowerCase().includes(q) ||
            (c.contact_person || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q)
        );
    }, [companies, search]);

    const totals = useMemo(() => ({
        companies: companies.length,
        seminars: companies.reduce((s, c) => s + (c.seminar_count || 0), 0),
        participants: companies.reduce((s, c) => s + (c.participant_count || 0), 0),
        revenue: companies.reduce((s, c) => s + (c.revenue || 0), 0),
    }), [companies]);

    return (
        <AdminLayout>
            <PageHeader
                title="Firmen (In-House)"
                description="Übersicht aller Unternehmen mit In-House-Fortbildungen — sortiert nach Umsatz."
            />

            {/* KPI-Zeile */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Firmen</div>
                    <div className="text-2xl font-bold" data-testid="kpi-companies">{totals.companies}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Kurse</div>
                    <div className="text-2xl font-bold" data-testid="kpi-seminars">{totals.seminars}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Teilnehmer</div>
                    <div className="text-2xl font-bold" data-testid="kpi-participants">{totals.participants}</div>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Euro className="h-3.5 w-3.5" /> Umsatz</div>
                    <div className="text-2xl font-bold" data-testid="kpi-revenue">{eur(totals.revenue)}</div>
                </CardContent></Card>
            </div>

            <div className="relative mb-4">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                    className="pl-8"
                    placeholder="Firma, Ansprechpartner oder E-Mail suchen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="companies-search"
                />
            </div>

            {loading ? (
                <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
                <Card>
                    <CardContent className="p-12 text-center text-muted-foreground text-sm" data-testid="companies-empty">
                        <Inbox className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        {companies.length === 0 ? 'Noch keine In-House-Firma erfasst.' : 'Keine Treffer für die Suche.'}
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="p-0 divide-y" data-testid="companies-list">
                        {filtered.map((c) => {
                            const key = c.name.toLowerCase();
                            const open = expandedKey === key;
                            return (
                                <div key={key} data-testid={`company-row-${key}`}>
                                    <button
                                        type="button"
                                        onClick={() => setExpandedKey(open ? null : key)}
                                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                                    >
                                        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                            <Building2 className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{c.name}</div>
                                            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                                {c.contact_person && <span>{c.contact_person}</span>}
                                                {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                                                {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                                                <span>Letzte Aktivität: {fmtDate(c.last_activity)}</span>
                                            </div>
                                        </div>
                                        <div className="hidden sm:flex flex-col items-end shrink-0 text-right">
                                            <div className="text-lg font-bold">{eur(c.revenue)}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {c.seminar_count} Kurs{c.seminar_count === 1 ? '' : 'e'} · {c.participant_count} TN
                                            </div>
                                        </div>
                                    </button>
                                    {open && (
                                        <div className="bg-muted/20 px-4 pb-4 pt-1" data-testid={`company-detail-${key}`}>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 pt-2">
                                                Kurse mit dieser Firma
                                            </div>
                                            <div className="grid gap-2">
                                                {(c.seminars || []).map((s) => (
                                                    <Link
                                                        key={s.id}
                                                        to={`/admin/seminars/${s.id}`}
                                                        className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-card hover:border-primary/50 transition-colors group"
                                                        data-testid={`company-seminar-${s.id}`}
                                                    >
                                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium truncate">{s.title}</div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {s.course_number || '—'} · {s.date || 'Datum offen'}
                                                                {s.is_cancelled && <span className="ml-2 text-red-700 font-medium">· Abgesagt</span>}
                                                            </div>
                                                        </div>
                                                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                                                    </Link>
                                                ))}
                                            </div>
                                            <div className="mt-3 text-xs text-muted-foreground">
                                                {c.invoice_count > 0
                                                    ? `${c.invoice_count} Sammelrechnung${c.invoice_count === 1 ? '' : 'en'} erstellt.`
                                                    : 'Noch keine Sammelrechnung erstellt.'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>
            )}
        </AdminLayout>
    );
}
